import {
  Outlet,
  createFileRoute,
  redirect,
  useMatches,
  useNavigate,
} from "@tanstack/react-router";
import { Box, useMediaQuery } from "@mui/material";
import { isLoggedIn } from "../hooks/useAuth";
import IconSidebar from "@/components/layout/IconSidebar";
import BottomNav from "@/components/layout/BottomNav";
import useAuth from "../hooks/useAuth";
import { useOrganization } from "../contexts/OrganizationContext";
import { useState, Suspense } from "react";
import {
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  CircularProgress,
  Box as MuiBox,
} from "@mui/material";
import Check from "@mui/icons-material/Check";
import Add from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import CreateOrganizationDialog from "@/components/CreateOrganizationDialog";
import CrispChat from "@/components/CrispChat";

export const Route = createFileRoute("/_layout")({
  component: Layout,
  beforeLoad: async () => {
    if (!isLoggedIn()) {
      throw redirect({
        to: "/login",
      });
    }
  },
});

function RouteFallback() {
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
      {prefersReducedMotion ? (
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      ) : (
        <CircularProgress size={32} />
      )}
    </Box>
  );
}

function Layout() {
  const { t } = useTranslation("billing");
  const { user } = useAuth();
  const {
    organizations,
    currentOrganization,
    setCurrentOrganization,
    isLoading,
  } = useOrganization();
  const matches = useMatches();
  const currentPath = matches[matches.length - 1]?.pathname;
  const [orgMenuAnchor, setOrgMenuAnchor] = useState<null | HTMLElement>(null);
  const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false);
  const navigate = useNavigate();

  const isDesktop = useMediaQuery("(min-width: 1024px)");

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        background:
          "linear-gradient(135deg, #050810 0%, #0a0e1a 50%, #050810 100%)",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Skip to main content link — WCAG AA */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          top: "-40px",
          left: 0,
          bgcolor: "primary.main",
          color: "white",
          p: "8px 16px",
          zIndex: 9999,
          fontSize: 14,
          textDecoration: "none",
          transition: "top 0.2s",
          "&:focus": { top: 0 },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
        }}
      >
        {t("layout.skipToContent")}
      </Box>

      {isDesktop && (
        <IconSidebar
          user={user}
          currentOrganization={currentOrganization}
          onOrgMenuOpen={(e) => setOrgMenuAnchor(e.currentTarget)}
          currentPath={currentPath}
        />
      )}
      <Box
        id="main-content"
        tabIndex={-1}
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          "&:focus": { outline: "none" },
        }}
      >
        {/* Outlet — each page type manages its own scroll */}
        <Box sx={{ flex: 1, minHeight: 0, position: "relative", pb: isDesktop ? 0 : "72px" }}>
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>

      {/* Organization Switcher Menu */}
      <MuiMenu
        anchorEl={orgMenuAnchor}
        open={Boolean(orgMenuAnchor)}
        onClose={() => setOrgMenuAnchor(null)}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0,
            ml: 1,
            minWidth: 200,
            background: "linear-gradient(145deg, #0f1420 0%, #141824 100%)",
            border: "1px solid rgba(59, 130, 246, 0.2)",
          },
        }}
      >
        <MuiBox sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Switch Organization
          </Typography>
        </MuiBox>
        <Divider sx={{ borderColor: "rgba(59, 130, 246, 0.1)" }} />
        {isLoading ? (
          <MuiBox sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </MuiBox>
        ) : (
          organizations.map((org) => (
            <MuiMenuItem
              key={org.id}
              onClick={() => {
                setCurrentOrganization(org);
                setOrgMenuAnchor(null);

                // Redirect to projects list if we're on a project detail or org-specific page
                if (
                  currentPath &&
                  (currentPath.match(/^\/projects\/[^/]+/) ||
                    currentPath.startsWith("/organizations/"))
                ) {
                  navigate({ to: "/projects" });
                }
              }}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                "&:hover": {
                  background: "rgba(59, 130, 246, 0.1)",
                },
              }}
            >
              <ListItemText primary={org.name} />
              {currentOrganization?.id === org.id && (
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <Check fontSize="small" sx={{ color: "#3b82f6" }} />
                </ListItemIcon>
              )}
            </MuiMenuItem>
          ))
        )}
        <Divider sx={{ borderColor: "rgba(59, 130, 246, 0.1)" }} />
        <MuiMenuItem
          onClick={() => {
            setOrgMenuAnchor(null);
            setCreateOrgDialogOpen(true);
          }}
          sx={{
            "&:hover": {
              background: "rgba(59, 130, 246, 0.1)",
            },
          }}
        >
          <ListItemIcon>
            <Add fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Create organization" />
        </MuiMenuItem>
      </MuiMenu>

      <CreateOrganizationDialog
        open={createOrgDialogOpen}
        onClose={() => setCreateOrgDialogOpen(false)}
      />

      {!isDesktop && (
        <BottomNav
          currentOrganization={currentOrganization}
          currentPath={currentPath}
          onOrgMenuOpen={(e) => setOrgMenuAnchor(e.currentTarget)}
        />
      )}

      <CrispChat />
    </Box>
  );
}

export default Layout;
